import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Typography,
  Box,
  Button,
  Paper,
  Modal,
  IconButton,
  Divider,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  useRoleListQuery,
  usePermissionListQuery,
  useLazyPermissionGetQuery,
  usePermissionUpdateMutation,
  usePermissionCreateMutation,
} from "../api/api";
import { ConfigurableListItem } from "./ConfigurableListItem";
import { RelationsEditorModal } from "./RelationsEditorModal";
import { SearchField } from "./SearchField";
import { useListFilter } from "./useListFilter";

const bgColor = "#EDEDED";

const permissionNamePattern = /^[A-Za-z.:\-\s]+$/;
const displayNamePattern = /^[А-Яа-яA-Za-z.:\-\s]+$/;

type TCreatePermissionForm = {
  name: string;
  displayable_name: string;
  description: string;
};

export const PermissionsList = () => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: rolesData,
    isLoading: isRolesLoading,
    isError: isRolesError,
  } = useRoleListQuery();

  const {
    data: permissionListData,
    isLoading: isPermissionListLoading,
    isError: isPermissionListError,
  } = usePermissionListQuery();

  const [
    getPermission,
    {
      data: permissionGetData,
      isLoading: isPermissionGetLoading,
      isError: isPermissionGetError,
    },
  ] = useLazyPermissionGetQuery();

  const [permissionUpdate, { isLoading: isPermissionUpdateLoading }] =
    usePermissionUpdateMutation();

  const [permissionCreate, { isLoading: isPermissionCreateloading }] =
    usePermissionCreateMutation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddPermissionModalOpen, setIsAddPermissionModalOpen] =
    useState(false);
  const [selectedPermissionTitle, setSelectedPermissionTitle] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
  const permissionRequestIdRef = useRef(0);

  const permission = permissionGetData?.data;

  const {
    search: permissionSearch,
    setSearch: setPermissionSearch,
    clearSearch: clearPermissionSearch,
    filteredItems: filteredPermissions,
    isFilteredEmpty: isPermissionsFilteredEmpty,
  } = useListFilter({
    items: permissionListData?.data,
    getSearchValue: (permissionItem) => permissionItem.name,
  });

  const {
    search: roleSearch,
    setSearch: setRoleSearch,
    clearSearch: clearRoleSearch,
    filteredItems: filteredRoles,
    isFilteredEmpty: isRolesFilteredEmpty,
  } = useListFilter({
    items: rolesData?.data,
    getSearchValue: (role) => role.displayable_name || role.name,
  });

  const methods = useForm<TCreatePermissionForm>({
    defaultValues: {
      displayable_name: "",
      name: "",
      description: "",
    },
    mode: "all",
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, isValid, errors },
  } = methods;

  const handleOpenModal = async (id: number) => {
    const requestId = ++permissionRequestIdRef.current;
    const permissionItem = permissionListData?.data.find(
      (item) => item.id === id,
    );

    setSelectedPermissionTitle(permissionItem?.name || "");
    setSelectedRoles([]);
    setIsModalOpen(true);
    clearRoleSearch();

    try {
      const response = await getPermission(id).unwrap();

      if (requestId !== permissionRequestIdRef.current) return;

      setSelectedRoles(response.data.roles.map((role) => role.id));
    } catch {
      if (requestId !== permissionRequestIdRef.current) return;
    }
  };

  const handleCloseModal = () => {
    permissionRequestIdRef.current += 1;

    setIsModalOpen(false);
    setSelectedPermissionTitle("");
    setSelectedRoles([]);
    clearRoleSearch();
  };

  const handleOpenAddPermissionModal = () => {
    setIsAddPermissionModalOpen(true);
  };

  const handleCloseAddPermissionModal = () => {
    setIsAddPermissionModalOpen(false);
    reset();
  };

  const handleToggleRole = (roleId: number) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId],
    );
  };

  const handleSaveNewPermission = async (values: TCreatePermissionForm) => {
    try {
      await permissionCreate({
        name: values.name,
        displayable_name: values.displayable_name,
        description: values.description,
        roles: [],
      }).unwrap();

      enqueueSnackbar("Успех", {
        variant: "success",
      });

      handleCloseAddPermissionModal();
    } catch (e) {
      console.error("Ошибка при создании пермишена", e);

      enqueueSnackbar("Ошибка", {
        variant: "error",
      });
    }
  };

  const handleSave = async () => {
    if (!permission) return;

    try {
      await permissionUpdate({
        permissionId: permission.id,
        body: {
          displayable_name: permission.displayable_name,
          description: permission.description,
          roles: selectedRoles,
        },
      }).unwrap();

      enqueueSnackbar("Успех", {
        variant: "success",
      });

      handleCloseModal();
    } catch (e) {
      console.error("Ошибка при обновлении пермишена", e);

      enqueueSnackbar("Ошибка", {
        variant: "error",
      });
    }
  };

  const isSubmitNewPermissionDisabled =
    !isDirty || !isValid || isPermissionCreateloading;

  if (isPermissionListLoading) {
    return <Typography>Загрузка пермишенов...</Typography>;
  }

  if (isPermissionListError) {
    return <Typography color="error">Ошибка загрузки пермишенов</Typography>;
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            mb: 7,
            fontSize: "1.25rem",
            fontWeight: 500,
            color: "#5F6368",
          }}
        >
          Пермишены
        </Typography>

        <Button
          variant="contained"
          onClick={handleOpenAddPermissionModal}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 2,
            py: 2,
            bgcolor: "#00719CDE",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#13779d",
              boxShadow: "none",
            },
          }}
        >
          + Добавить пермишен
        </Button>
      </Box>

      <SearchField
        value={permissionSearch}
        onChange={setPermissionSearch}
        onClear={clearPermissionSearch}
        placeholder="Поиск пермишена"
        sx={{ mb: 4 }}
      />

      <Paper
        elevation={3}
        sx={{
          p: 4.25,
          pr: 0,
          borderRadius: 3,
          bgcolor: "common.white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            maxHeight: 615,
            overflowY: "auto",
          }}
        >
          {filteredPermissions.map((permissionItem) => (
            <ConfigurableListItem
              key={permissionItem.id}
              id={permissionItem.id}
              title={permissionItem.name}
              color={bgColor}
              onConfigure={handleOpenModal}
            />
          ))}

          {isPermissionsFilteredEmpty && (
            <Typography color="text.secondary">
              Нет подходящих данных
            </Typography>
          )}
        </Box>
      </Paper>

      <RelationsEditorModal
        open={isModalOpen}
        title="Пермишен"
        entityTitle={selectedPermissionTitle}
        relationTitle="Роли"
        relationIcon={
          <SwapHorizIcon sx={{ fontSize: 36, color: "text.secondary" }} />
        }
        items={filteredRoles}
        selectedIds={selectedRoles}
        searchValue={roleSearch}
        searchPlaceholder="Поиск роли"
        isFilteredEmpty={isRolesFilteredEmpty}
        isLoading={isPermissionGetLoading || isRolesLoading}
        isError={isPermissionGetError || isRolesError}
        isSaving={isPermissionUpdateLoading}
        loadingText="Загрузка ролей пермишена..."
        errorText="Ошибка загрузки ролей пермишена"
        getItemLabel={(role) => role.displayable_name || role.name}
        onSearchChange={setRoleSearch}
        onSearchClear={clearRoleSearch}
        onToggle={handleToggleRole}
        onSave={handleSave}
        onClose={handleCloseModal}
      />

      <Modal
        open={isAddPermissionModalOpen}
        onClose={handleCloseAddPermissionModal}
      >
        <Box
          component="form"
          onSubmit={handleSubmit(handleSaveNewPermission)}
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 560,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            outline: "none",
          }}
        >
          <IconButton
            onClick={handleCloseAddPermissionModal}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ px: 3, py: 4 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
              Новый Пермишен
            </Typography>
          </Box>

          <Divider />

          <Box sx={{ px: 3, py: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "180px 1fr",
                rowGap: 3,
                columnGap: 3,
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: 20, color: "#5F6368" }}>
                Название Пермишена
              </Typography>

              <Controller
                control={control}
                name="displayable_name"
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    placeholder="Просмотр транзакций"
                    helperText={fieldState.error?.message}
                    error={!!errors.displayable_name}
                    size="small"
                    inputProps={{ maxLength: 101 }}
                  />
                )}
                rules={{
                  required: "Обязательное поле",
                  maxLength: {
                    value: 100,
                    message: "Максимальная длина — 100 символов",
                  },
                  pattern: {
                    value: displayNamePattern,
                    message: "Допустимы только Aa-Яя . : -",
                  },
                }}
              />

              <Typography sx={{ fontSize: 20, color: "#5F6368" }}>
                Системное наименование
              </Typography>

              <Controller
                control={control}
                name="name"
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    placeholder="transactions.view"
                    helperText={fieldState.error?.message}
                    error={!!errors.name}
                    size="small"
                    inputProps={{ maxLength: 101 }}
                  />
                )}
                rules={{
                  required: "Обязательное поле",
                  maxLength: {
                    value: 100,
                    message: "Максимальная длина — 100 символов",
                  },
                  pattern: {
                    value: permissionNamePattern,
                    message: "Допустимы только Aa-Zz . : -",
                  },
                }}
              />

              <Typography
                sx={{ fontSize: 20, color: "#5F6368", alignSelf: "flex-start" }}
              >
                Описание
              </Typography>

              <Controller
                control={control}
                name="description"
                render={({ field: { ref, ...field }, fieldState }) => (
                  <TextField
                    inputRef={ref}
                    fullWidth
                    {...field}
                    helperText={fieldState.error?.message}
                    error={!!errors.description}
                    size="small"
                    inputProps={{ maxLength: 256 }}
                    multiline
                    rows={4}
                  />
                )}
                rules={{
                  maxLength: {
                    value: 255,
                    message: "Максимальная длина — 256 символов",
                  },
                  pattern: {
                    value: displayNamePattern,
                    message: "Допустимы только Aa-Яя . : -",
                  },
                }}
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitNewPermissionDisabled}
                sx={{
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  borderRadius: 1.5,
                  bgcolor: "#2388A8",
                  "&:hover": {
                    bgcolor: "#1d7895",
                  },
                }}
              >
                {isPermissionCreateloading ? "Сохранение..." : "Сохранить"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};
